import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import './Auth.css';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const from = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(form.email, form.password); navigate(from, { replace: true }); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="auth-om">ॐ</span>
          <h1 className="auth-title-hi">राघवेंद्र पूजन भंडार</h1>
          <p className="auth-subtitle">{t({ hi:'लॉगिन करें', en:'Sign In to Your Account' })}</p>
        </div>
        {error && <div className="auth-error">⚠️ {error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t({ hi:'ईमेल', en:'Email' })}</label>
            <input type="email" className="form-input" placeholder="you@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
          </div>
          <div className="form-group">
            <label className="form-label">{t({ hi:'पासवर्ड', en:'Password' })}</label>
            <div className="input-password-wrap">
              <input type={showPwd?'text':'password'} className="form-input" placeholder="••••••••" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required autoComplete="current-password" />
              <button type="button" className="pwd-toggle" onClick={() => setShowPwd(!showPwd)}>{showPwd?<EyeOff size={15}/>:<Eye size={15}/>}</button>
            </div>
          </div>
          <div className="auth-forgot"><Link to="/forgot-password">{t({ hi:'पासवर्ड भूल गए?', en:'Forgot password?' })}</Link></div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? <span className="auth-spinner"/> : <><LogIn size={15}/> {t({ hi:'लॉगिन करें', en:'Sign In' })}</>}
          </button>
        </form>
        <div className="auth-demo">
          <p>Demo Admin: admin@raghavendra.com / Admin@12345</p>
        </div>
        <div className="auth-switch">
          {t({ hi:'नए उपयोगकर्ता?', en:"Don't have an account?" })} <Link to="/register">{t({ hi:'पंजीकरण करें', en:'Register' })}</Link>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ name:'', email:'', phone:'', password:'', confirmPassword:'', preferredLang:'hi', isNRI:false });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (form.password !== form.confirmPassword) { setError(t({ hi:'पासवर्ड मेल नहीं खाते', en:'Passwords do not match' })); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try { const { confirmPassword, ...data } = form; await register(data); navigate('/'); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        <div className="auth-brand">
          <span className="auth-om">ॐ</span>
          <h1 className="auth-title-hi">राघवेंद्र पूजन भंडार</h1>
          <p className="auth-subtitle">{t({ hi:'नया खाता बनाएं', en:'Create Your Account' })}</p>
        </div>
        {error && <div className="auth-error">⚠️ {error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t({ hi:'पूरा नाम', en:'Full Name' })} *</label><input type="text" className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="form-group"><label className="form-label">{t({ hi:'फ़ोन', en:'Phone' })}</label><input type="tel" className="form-input" placeholder="+91..." value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="form-group"><label className="form-label">{t({ hi:'ईमेल', en:'Email' })} *</label><input type="email" className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t({ hi:'पासवर्ड', en:'Password' })} *</label>
              <div className="input-password-wrap"><input type={showPwd?'text':'password'} className="form-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={6} /><button type="button" className="pwd-toggle" onClick={() => setShowPwd(!showPwd)}>{showPwd?<EyeOff size={15}/>:<Eye size={15}/>}</button></div>
            </div>
            <div className="form-group"><label className="form-label">{t({ hi:'पासवर्ड दोहराएं', en:'Confirm' })} *</label><input type="password" className="form-input" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} required /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t({ hi:'भाषा', en:'Language' })}</label><select className="form-input" value={form.preferredLang} onChange={e => setForm({ ...form, preferredLang: e.target.value })}><option value="hi">हिंदी</option><option value="en">English</option></select></div>
            <div className="form-group nri-group"><label className="form-label">NRI?</label><label className="toggle-label"><input type="checkbox" checked={form.isNRI} onChange={e => setForm({ ...form, isNRI: e.target.checked })} /><span className="toggle-track"><span className="toggle-thumb"/></span><span style={{ fontSize:'.8rem' }}>{form.isNRI?'Yes':'No'}</span></label></div>
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? <span className="auth-spinner"/> : `✨ ${t({ hi:'खाता बनाएं', en:'Create Account' })}`}
          </button>
        </form>
        <div className="auth-switch">{t({ hi:'पहले से खाता है?', en:'Already have an account?' })} <Link to="/login">{t({ hi:'लॉगिन करें', en:'Sign In' })}</Link></div>
      </div>
    </div>
  );
}
